/**
 * WHAT A JOB COSTS, DECIDED IN ONE PLACE.
 *
 * Ported from the old lib-quote-draft.js after reading it properly, not
 * reinvented. Every rule here exists because something went wrong without
 * it, and the notes say which — a rule whose reason is lost gets deleted
 * by the next person who finds it inconvenient.
 *
 * The reason this is a MODULE and not code inside the quote drafter: the
 * old system priced a part one way in the drafter and another way in the
 * quote builder, and the two disagreed by 60% on the same part for 342
 * quotes in a single day. Nobody noticed because both looked reasonable in
 * isolation. One definition, used by both doors, is the whole fix.
 */

export type Tier = {
  cost_from: number;
  cost_to: number | null;
  multiplier: number;
  min_margin: number;
};

export type FlatPrice = { label: string; keywords: string[]; price: number };

export const FALLBACK_MULTIPLIER = 1.35;

/**
 * A part's price, from what the shop paid.
 *
 * Banded, because a $20 part and a $900 part do not carry the same margin.
 * The band with the highest `cost_from` that still contains the cost wins,
 * and `min_margin` is a floor in money — some cheap parts are not worth
 * fitting for 35% of very little.
 *
 * NO TIERS IS NOT ZERO. An empty list falls back to x1.35 and the caller is
 * expected to say so out loud, because the old system's silent fallback is
 * exactly how it quoted $27 for a $44 part.
 */
export function sellPrice(tiers: Tier[], cost: number | null | undefined): number {
  const c = Number(cost) || 0;
  if (!c) return 0;
  const band = (tiers ?? [])
    .filter((t) => c >= Number(t.cost_from) && (t.cost_to == null || c < Number(t.cost_to)))
    .sort((a, b) => Number(b.cost_from) - Number(a.cost_from))[0];
  if (!band) return round2(c * FALLBACK_MULTIPLIER);
  return round2(Math.max(c * Number(band.multiplier), c + Number(band.min_margin ?? 0)));
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Words, lowercased and stripped, so "pre-purchase" and "pre purchase" match. */
const words = (s: string) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/**
 * Work the shop charges a fixed price for, which the hourly rate does not
 * touch. A pre-purchase inspection is $225 whether it takes ninety minutes
 * or three hours, and pricing it by the hour is how the shop loses on the
 * long ones and overcharges on the short ones.
 */
export function flatPriceFor(prices: FlatPrice[], task: string): FlatPrice | null {
  const t = words(task);
  if (!t) return null;
  for (const p of prices ?? []) {
    if ((p.keywords ?? []).some((k) => k && t.includes(words(k)))) return p;
  }
  return null;
}

export type LaborLine = {
  task: string;
  hours: number;
  /** A line's own rate beats the shop rate — that is what a flat price is. */
  rate?: number;
  flat?: string;
  diagnostic?: boolean;
};

/** A line's own rate when it has one, the shop's when it does not. */
export const lineRate = (l: LaborLine, shopRate: number): number =>
  l && Number(l.rate) > 0 ? Number(l.rate) : shopRate;

const IS_DIAGNOSTIC = /\b(diagnos|diagnostic|scan|inspect the fault|find the fault|trace)/i;

/**
 * DIAGNOSIS IS ONE HOUR, AND THERE IS ONLY ONE OF IT.
 *
 * However long a hard fault might take, the shop charges an hour to find
 * out what is wrong. A flat-priced line is left alone — an inspection is
 * not a diagnostic hour wearing a different name.
 *
 * And only ONE line survives. Two diagnosis lines on one quote is the
 * estimator padding, and the second is dropped rather than clamped to
 * another hour, because clamping it still bills for it.
 */
export function oneHourOfDiagnosis(lines: LaborLine[]): { lines: LaborLine[]; hadDiagnostic: boolean } {
  let seen = false;
  const out: LaborLine[] = [];
  for (const l of lines ?? []) {
    if (l.flat || !IS_DIAGNOSTIC.test(String(l.task ?? ""))) { out.push(l); continue; }
    if (seen) continue;
    seen = true;
    out.push({ ...l, hours: 1, diagnostic: true });
  }
  return { lines: out, hadDiagnostic: seen };
}

/**
 * What the customer is told about that hour.
 *
 * The rule is only half a rule if the second half is never said. Unsaid it
 * reads as a fee for nothing; said, it is the reason to come back.
 */
export const DIAGNOSTIC_CREDIT =
  "The diagnostic hour comes off the repair if you have the work done with us.";

/** The whole labor side of a quote, priced. */
export function priceLabor(
  raw: LaborLine[], shopRate: number, flats: FlatPrice[],
): { lines: Array<LaborLine & { total: number }>; note: string | null } {
  const claimed = new Set<string>();
  const withFlats: LaborLine[] = [];
  for (const l of raw ?? []) {
    const flat = flatPriceFor(flats, l.task);
    if (flat) {
      // One of each flat price per quote. Two inspections on one car is
      // not two inspections.
      if (claimed.has(flat.label)) continue;
      claimed.add(flat.label);
      withFlats.push({ task: l.task, hours: 1, rate: flat.price, flat: flat.label });
    } else withFlats.push(l);
  }

  const capped = oneHourOfDiagnosis(withFlats);
  return {
    lines: capped.lines.map((l) => ({ ...l, total: round2(Number(l.hours) * lineRate(l, shopRate)) })),
    note: capped.hadDiagnostic ? DIAGNOSTIC_CREDIT : null,
  };
}
